import AIcon from '@/components/A/AIcon/AIcon'
import styles from './InfoBindPageMobile.module.less'
import AInput from '@/components/A/AInput/AInput'
import AButton from '@/components/A/AButton/AButton'
import { useEffect, useState } from 'react'
import { useRequest } from 'alova/client'
import { api } from '@/api'
import { message } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { PAGE_LINKS } from '@/router/pageConst'
import DefaultAvatar from '@/components/DefaultAvatar/DefaultAvatar'
import { commonUtils } from '@/helpers/commonUtils'
import { useTranslation } from 'react-i18next'
import { FormItem, FormRow } from '@/pages/ProjectCreatePage/components/StepOne/StepOne'
import { observer } from 'mobx-react'
import DropdownSelector from '@/components/ApplyTrialBox/components/DropdownSelector/DropdownSelector'
import useTrialOptions from '@/helpers/commonHooks/useTrialOptions'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import { userService } from '@/services/userService'
import { trackingService } from '@/services/trackingService'

function InfoBindPageMobile() {
  const { t } = useTranslation()
  const { COUNTRY_OPTIONS } = useTrialOptions()
  const navigate = useNavigate()
  const { data: tmpUserInfo } = useRequest(api.auth.tmpUserInfo)
  const { data: userBindData, send: getUserBindStatus } = useRequest(api.auth.userBindStatus)
  const [errorKeys, setErrorKeys] = useState<Array<'username' | 'corpName' | 'location' | 'code'>>(
    []
  )
  const [username, setUsername] = useState<string | null>(null)
  const [corpName, setCorpName] = useState<string | null>(null)
  const [location, setLocation] = useState<string | null>(null)
  const [invitationCode, setInvitationCode] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [mode, setMode] = useState<'form' | 'refresh' | 'deny'>('form')
  const [isFrom, setIsFrom] = useTypedLocalStorage('is_from', { defaultValue: '' })

  const handleCreate = async () => {
    const keys = validate(true)
    if (keys.length > 0) {
      return
    }
    setLoading(true)
    try {
      await api.auth
        .userBindCorp({
          location: location!,
          staffName: username!,
          corpName: corpName!,
          invitationCode,
          ...(isFrom === 'aigc' ? { isFrom: 'aigc' } : {}),
        })
        .send()
      if (invitationCode) {
        setMode('refresh')
        message.success(t('infoBindPage.message.success'))
      } else {
        message.success(t('infoBindPage.message.success2'))
        await userService.refreshUserInfo()
        trackingService.trackRegisterComplete()
        if (isFrom) {
          setIsFrom('')
          navigate(PAGE_LINKS.freeTrial, { state: { forStatus: true } })
        } else {
          navigate(PAGE_LINKS.index)
        }
      }
    } catch (e) {
      const error = e as ApiError
      if (error.statusCode === 40001) {
        setErrorKeys((keys) => [...keys, 'code'])
      } else if (error.statusCode === 40002) {
        message.error(t('infoBindPage.message.needRegisterAgain'))
        navigate(PAGE_LINKS.login, { replace: true, state: { mode: 'sign' } })
      } else if (error.statusCode === 40003) {
        message.error(t('infoBindPage.message.alreadyBind'))
      } else {
        setErrorKeys((keys) => keys.filter((k) => k !== 'code'))
        message.error(t('infoBindPage.message.error'))
      }
    }
    setLoading(false)
  }

  const handleReapply = () => {
    setMode('form')
  }

  const handleCreateAccountDirectly = () => {
    api.auth
      .insertSessionAfterRejectBindRequest()
      .send()
      .then(() => {
        trackingService.trackRegisterComplete()
        navigate(PAGE_LINKS.index)
        message.success(t('infoBindPage.message.accountCreatedDirectly'))
      }).catch((e) => {
        const error = e as ApiError
        if (error.statusCode === 40001) {
          message.error(error.statusMessage)
          setErrorKeys((keys) => [...keys, 'code'])
        } else {
          setErrorKeys((keys) => keys.filter((k) => k !== 'code'))
          message.error(t('infoBindPage.message.error'))
        }
      })
  }

  const validate = (force?: boolean) => {
    let keys = errorKeys
    if (force) {
      if (!username) {
        keys.push('username')
      } else {
        keys = keys.filter((k) => k !== 'username')
      }
      if (!corpName) {
        keys.push('corpName')
      } else {
        keys = keys.filter((k) => k !== 'corpName')
      }
      if (!location) {
        keys.push('location')
      } else {
        keys = keys.filter((k) => k !== 'location')
      }
    } else {
      if (username === '') {
        keys.push('username')
      } else {
        keys = keys.filter((k) => k !== 'username')
      }
      if (corpName === '') {
        keys.push('corpName')
      } else {
        keys = keys.filter((k) => k !== 'corpName')
      }
      if (location === '') {
        keys.push('location')
      } else {
        keys = keys.filter((k) => k !== 'location')
      }
    }
    setErrorKeys(Array.from(new Set(keys)))
    return Array.from(new Set(keys))
  }

  useEffect(() => {
    if (!userBindData) {
      return
    }
    if (userBindData.status === 'bound') {
      if (mode === 'refresh') {
        trackingService.trackRegisterComplete()
      }
      message.success(t('infoBindPage.message.theRequestHasBeenApprovedAndIsJumpingThePageForYou'))
      navigate(PAGE_LINKS.index)
    }
    if (userBindData.status === 'apply_reject') {
      setMode('deny')
    }
    if (userBindData.status === 'applying') {
      message.warning(t('infoBindPage.message.theRequestHasBeenSendAndPleaseBePatient'))
      setMode('refresh')
    }
    if (userBindData.status === 'unbound') {
      setMode('form')
    }
  }, [userBindData])

  useEffect(() => {
    validate()
  }, [username, corpName, location])

  useEffect(() => {
    if (tmpUserInfo && !tmpUserInfo.isAuth) {
      message.warning(t('infoBindPage.message.wrongRouting'))
      navigate(PAGE_LINKS.index)
      return
    }
  }, [tmpUserInfo])

  return (
    <div className={styles.infoBindPage} data-testid='info-bind-page'>
      <div className={styles.infoBindPageContent}>
        <img className={styles.bg} src={commonUtils.getImagePath('pricingPageMobile/pricing-page-bg.png')} alt="" />
        <div className={styles.bindInfoBox}>
          <div className={styles.rightBox}>
            <div className={styles.rightBoxInner}>
              <img
                className={styles.brand}
                src={commonUtils.getImagePath('loginPage/login-brand.png')}
                alt=''
              />
              <p className={styles.title}>{t('infoBindPage.joinAdEff')}</p>
              {tmpUserInfo && tmpUserInfo.isAuth && (
                <div className={styles.infoCard}>
                  <DefaultAvatar
                    width={36}
                    height={36}
                    avatar={tmpUserInfo.data.avatar}
                    name={tmpUserInfo.data.name || tmpUserInfo.data.email}
                  />
                  <div className={styles.emailInfo}>
                    {tmpUserInfo.data.name && (
                      <p className={styles.name}>{tmpUserInfo.data.name}</p>
                    )}
                    <p className={styles.email}>{tmpUserInfo.data.email}</p>
                  </div>
                </div>
              )}
              {mode === 'form' && (
                <>
                  <div className={styles.infoBox}>
                    <FormRow className={styles.formRow}>
                      <FormItem
                        label={t('infoBindPage.username')}
                        labelRender={(label) => <div className={styles.formItemLabel}>{label}</div>}
                        required
                        error={errorKeys.includes('username')}
                        errorMsg={t('infoBindPage.message.thisFieldIsRequired')}
                        errorMsgRender={(msg) => <span className={styles.formItemErrorMsg}>{msg}</span>}
                      >
                        <AInput
                          testId='info-bind-username-input'
                          className={styles.inputWidth}
                          placeholder={t('infoBindPage.message.pleaseEnterTheUsername')}
                          onChange={(e) => {
                            setUsername(e.target.value)
                          }}
                          onBlur={() => {
                            window.scrollTo(0, 0)
                          }}
                        />
                      </FormItem>
                    </FormRow>
                    <FormRow className={styles.formRow}>
                      <FormItem
                        label={t('infoBindPage.company')}
                        labelRender={(label) => <div className={styles.formItemLabel}>{label}</div>}
                        required
                        error={errorKeys.includes('corpName')}
                        errorMsg={t('infoBindPage.message.thisFieldIsRequired')}
                        errorMsgRender={(msg) => <span className={styles.formItemErrorMsg}>{msg}</span>}
                      >
                        <AInput
                          testId='info-bind-company-input'
                          className={styles.inputWidth}
                          placeholder={t('infoBindPage.message.pleaseEnterTheCompanyName')}
                          onChange={(e) => {
                            setCorpName(e.target.value)
                          }}
                          onBlur={() => {
                            window.scrollTo(0, 0)
                          }}
                        />
                      </FormItem>
                    </FormRow>
                    <FormRow className={styles.formRow}>
                      <FormItem
                        label={t('infoBindPage.placeOfResidence')}
                        labelRender={(label) => <div className={styles.formItemLabel}>{label}</div>}
                        required
                        error={errorKeys.includes('location')}
                        errorMsg={t('infoBindPage.message.thisFieldIsRequired')}
                        errorMsgRender={(msg) => <span className={styles.formItemErrorMsg}>{msg}</span>}
                      >
                        <DropdownSelector
                          testId='info-bind-location-selector'
                          options={COUNTRY_OPTIONS}
                          className={styles.dropdownSelector}
                          menuClassName={styles.dropdownMenu}
                          placeholder={t('infoBindPage.message.pleaseEnterTheResidence')}
                          otherPlaceholder={t('modals.freeTrialModal.applyBox.pleaseEnter')}
                          onChange={(opt) => {
                            let location = ''
                            if (opt.value === 'Other') {
                              location = opt.label
                            } else {
                              location = commonUtils.transferCountryKeyToZhTranslateText(opt.value)
                            }
                            setLocation(location)
                          }}
                          onBlur={() => {
                            window.scrollTo(0, 0)
                          }}
                        />
                      </FormItem>
                    </FormRow>
                    <FormRow className={styles.formRow}>
                      <FormItem
                        label={t('infoBindPage.invitationCode')}
                        labelRender={(label) => <div className={styles.formItemLabel}>{label}</div>}
                        error={errorKeys.includes('location')}
                        errorMsg={t('infoBindPage.message.invitationCodeDoesNotExist')}
                        errorMsgRender={(msg) => <span className={styles.formItemErrorMsg}>{msg}</span>}
                      >
                        <AInput
                          testId='info-bind-invitation-code-input'
                          className={styles.inputWidth}
                          placeholder={t(
                            'infoBindPage.message.ifYourCompanyHaveBeenJoinedPleaseWriteHere'
                          )}
                          onChange={(e) => {
                            setInvitationCode(e.target.value)
                            setErrorKeys((keys) => keys.filter((k) => k !== 'code'))
                          }}
                          onBlur={() => {
                            window.scrollTo(0, 0)
                          }}
                        />
                      </FormItem>
                    </FormRow>
                  </div>
                  <AButton
                    testId='info-bind-create-btn'
                    className={styles.createBtn}
                    type='primary'
                    size='LGR'
                    loading={loading}
                    onClick={handleCreate}
                  >
                    {t('infoBindPage.createAccountForFree')}
                  </AButton>
                </>
              )}
              {mode === 'refresh' && (
                <div className={styles.refreshBox}>
                  <img
                    className={styles.refreshPic}
                    src={commonUtils.getImagePath('infoBindPage/info-bind-refresh.png')}
                    alt=''
                  />
                  <p className={styles.refreshDesc}>
                    {t('infoBindPage.message.waitingForAdministratorReview')}
                  </p>
                  <AButton
                    testId='info-bind-refresh-status-btn'
                    type='primary'
                    size='LG-AUTO'
                    className={styles.refreshBtn}
                    onClick={() => {
                      getUserBindStatus()
                    }}
                  >
                    <AIcon type='icon-line-global_refresh' />
                    <span>{t('infoBindPage.refreshStatus')}</span>
                  </AButton>
                </div>
              )}
              {mode === 'deny' && (
                <div className={styles.refreshBox}>
                  <img
                    className={styles.refreshPic}
                    src={commonUtils.getImagePath('infoBindPage/info-bind-deny.png')}
                    alt=''
                  />
                  <p className={styles.refreshDesc} style={{ width: 404 }}>
                    {t('infoBindPage.message.yourRequestHasBeenDeclined')}
                  </p>
                  <AButton
                    testId='info-bind-reapply-btn'
                    type='primary'
                    size='LG-AUTO'
                    className={styles.refreshBtn}
                    onClick={handleReapply}
                  >
                    {t('infoBindPage.reapply')}
                  </AButton>
                  <span className={styles.createAccount} onClick={handleCreateAccountDirectly} data-testid='info-bind-create-directly-btn'>
                    {t('infoBindPage.createAccountForFree')} <AIcon type='icon-line-global_arrow' />
                  </span>
                </div>
              )}
              <p className={styles.bottomTips}>
                {t('infoBindPage.bySigningUp')}
                <Link to={PAGE_LINKS.termsOfService} target='_blank'>
                  <span className={styles.cursorText}>{t('infoBindPage.termsOfService')}</span>
                </Link>
                {t('infoBindPage.and')}
                <Link to={PAGE_LINKS.privacy} target='_blank'>
                  <span className={styles.cursorText}>{t('infoBindPage.privacyPolicy')}</span>
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default observer(InfoBindPageMobile)
